import { useCallback, useMemo, useState } from 'react';
import { createDefaultFilters, DEFAULT_ROWS_PER_PAGE } from '../constants/paymentOrdersConstants';

const normalizeString = (value = '') => value.toLowerCase();

export const usePaymentOrdersController = (legalEntityMap = {}) => {
  const [filters, setFilters] = useState(createDefaultFilters);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

  const apiFilters = useMemo(() => {
    const payload = {};
    if (filters.start_date) payload.start_date = filters.start_date;
    if (filters.end_date) payload.end_date = filters.end_date;
    if (filters.uuid_legal_entity) payload.uuid_legal_entity = filters.uuid_legal_entity;
    if (filters.status && filters.status !== 'all') payload.status = filters.status;
    if (filters.cost_behavior) payload.cost_behavior = filters.cost_behavior;
    if (filters.cost_nature || filters.cost_traceability) {
      payload.cost_nature = filters.cost_nature || filters.cost_traceability;
      payload.cost_traceability = filters.cost_nature || filters.cost_traceability;
    }
    if (filters.cost_area) payload.cost_area = filters.cost_area;
    return payload;
  }, [filters]);

  const computeOrdersView = useCallback(
    (paymentOrders = []) => {
      const filtered = (() => {
        if (!searchTerm) return paymentOrders;
        const query = normalizeString(searchTerm);

        return paymentOrders.filter((order) => {
          const beneficiary = normalizeString(legalEntityMap[order.uuid_legal_entity] || '');
          const observations = normalizeString(order.observations || '');
          const applied = normalizeString(order.applied_to_description || '');
          const classification = normalizeString(
            `${order.cost_behavior || ''} ${order.cost_nature || order.cost_traceability || ''} ${order.cost_area || ''}`,
          );
          const orderNumber = String(order.order_number || '');

          return (
            beneficiary.includes(query) ||
            observations.includes(query) ||
            applied.includes(query) ||
            classification.includes(query) ||
            orderNumber.includes(query)
          );
        });
      })();

      const totalPages = filtered.length
        ? Math.max(1, Math.ceil(filtered.length / rowsPerPage))
        : 1;

      const safePage = Math.min(page, totalPages);
      const startIndex = (safePage - 1) * rowsPerPage;
      const paginated = filtered.slice(startIndex, startIndex + rowsPerPage);

      return {
        paginatedOrders: paginated,
        totalPages,
        currentPage: safePage,
        totalCount: filtered.length,
        rowsPerPage,
      };
    },
    [searchTerm, legalEntityMap, page, rowsPerPage],
  );

  const handleFilterChange = useCallback((event) => {
    const { name, value } = event.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
    setPage(1);
  }, []);

  const handleSearchChange = useCallback((value) => {
    setSearchTerm(value);
    setPage(1);
  }, []);

  const handlePageChange = useCallback((_, value) => {
    setPage(value);
  }, []);

  const handleRowsPerPageChange = useCallback((event) => {
    const nextValue = Number.parseInt(event.target.value, 10);
    setRowsPerPage(Number.isNaN(nextValue) ? DEFAULT_ROWS_PER_PAGE : nextValue);
    setPage(1);
  }, []);

  return {
    filters,
    apiFilters,
    searchTerm,
    setPage: handlePageChange,
    handleFilterChange,
    handleSearchChange,
    setSearchTerm,
    setFilters,
    rowsPerPage,
    handleRowsPerPageChange,
    DEFAULT_ROWS_PER_PAGE,
    computeOrdersView,
  };
};

export default usePaymentOrdersController;
